import type {Dispatch, SetStateAction} from 'react';
import {useCallback, useEffect, useRef} from 'react';

/**
 * A custom hook that handles detecting outside clicks for a given element.
 * It sets up a reference to the element and listens for `mousedown` events on the document.
 * If a click is detected outside of the referenced element, it triggers the provided `setIsOpen` callback to close the element.
 *
 * @param setIsOpen A function to update the state of the element, typically used to close it when an outside click is detected.
 * @param onClose   A callback function that is called when an outside click is detected, allowing for additional cleanup or actions to be performed.
 * @returns A ref object that can be attached to the element to monitor for outside clicks.
 *
 * @example
 * ```tsx
 * const MyDropdown = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useOutsideClick<HTMLDivElement>(setIsOpen);
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       {isOpen && <DropdownContent />}
 *     </div>
 *   );
 * };
 * ```
 */
export const useOutsideClick = <T extends HTMLElement>(
    setIsOpen: Dispatch<SetStateAction<boolean>>,
    onClose?: () => void
) => {
    const elemRef = useRef<T>(null);

    /**
     * This function detects if a click occurs outside of the referenced element.
     * If the click is outside, it updates the `setIsOpen` state to close the element.
     *
     * @param event The click event.
     */
    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (elemRef.current && !elemRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            if (onClose) onClose();
        }
    }, [setIsOpen, onClose]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    return elemRef;
};